import type { ZohoContact } from '@deltasuite/zoho-client';
import { masterMark } from '@deltasuite/ui-tokens';
import { useAuth } from '@deltasuite/auth';
import { useEffect, useRef, useState } from 'react';
import { CampaignsPage } from './campaigns/CampaignsPage';
import { DashboardPage } from './dashboard/DashboardPage';
import { HomeScreen } from './home/HomeScreen';
import { PreferencesPage } from './preferences/PreferencesPage';
import { SprintModeScreen } from './sprints/SprintModeScreen';
import { SprintRunner } from './sprints/runner/SprintRunner';

type SecondaryTab = 'campaigns' | 'dashboard' | 'preferences' | 'build';
type View =
  | { kind: 'home' }
  | { kind: 'tab'; tab: SecondaryTab }
  | { kind: 'sprint'; contacts: readonly ZohoContact[] };

const SECONDARY_TABS: readonly { id: SecondaryTab; label: string }[] = [
  { id: 'build', label: 'Build a call list' },
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'preferences', label: 'Settings' },
];

export function AppShell() {
  const [view, setView] = useState<View>({ kind: 'home' });

  if (view.kind === 'sprint') {
    return <SprintShell contacts={view.contacts} onExit={() => setView({ kind: 'home' })} />;
  }

  return (
    <div className="min-h-full">
      <Nav active={view.kind === 'home' ? 'home' : view.tab} onNavigate={setView} />
      {view.kind === 'home' ? (
        <HomeScreen
          onStart={(contacts) => setView({ kind: 'sprint', contacts })}
          onBuildList={() => setView({ kind: 'tab', tab: 'build' })}
        />
      ) : view.tab === 'build' ? (
        <SprintModeScreen onStart={(contacts) => setView({ kind: 'sprint', contacts })} />
      ) : view.tab === 'campaigns' ? (
        <CampaignsPage />
      ) : view.tab === 'dashboard' ? (
        <DashboardPage />
      ) : (
        <PreferencesPage />
      )}
    </div>
  );
}

// Top nav: brand on the left, an overflow menu on the right that holds the
// secondary screens. Everything except home is one click behind the menu —
// the cockpit is the front door.
function Nav({
  active,
  onNavigate,
}: {
  active: 'home' | SecondaryTab;
  onNavigate: (v: View) => void;
}) {
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const auth = useAuth();
  const signedIn = auth.state.status === 'signed_in';
  const userEmail = auth.state.status === 'signed_in' ? auth.state.session.user.email : null;

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const onSignOut = async () => {
    setSigningOut(true);
    try {
      await auth.signOut();
      // AuthGate flips to signed_out and re-renders LoginScreen automatically.
    } catch {
      // Quiet failure — user can refresh.
    } finally {
      setSigningOut(false);
      setOpen(false);
    }
  };

  return (
    <nav className="border-b border-graphite-100 bg-surface px-8 py-3">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => onNavigate({ kind: 'home' })}
          className="flex items-center gap-2 text-graphite-700 hover:text-graphite-900"
          aria-label="Home"
        >
          <span className="font-sans text-lg leading-none text-signal-amber" aria-hidden>
            {masterMark}
          </span>
          <span className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
            DeltaDial
          </span>
        </button>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-haspopup="menu"
            className="rounded-md px-3 py-1.5 font-sans text-sm text-graphite-500 hover:text-graphite-900"
          >
            Menu ≡
          </button>
          {open ? (
            <ul
              role="menu"
              className="absolute right-0 top-full z-10 mt-1 w-48 rounded-md border border-graphite-100 bg-surface py-1 shadow-md"
            >
              {SECONDARY_TABS.map((t) => {
                const isActive = active === t.id;
                return (
                  <li key={t.id} role="none">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        onNavigate({ kind: 'tab', tab: t.id });
                        setOpen(false);
                      }}
                      aria-current={isActive ? 'page' : undefined}
                      className={
                        'block w-full px-3 py-2 text-left font-sans text-sm transition-colors ' +
                        (isActive
                          ? 'bg-surface-sunk text-graphite-900'
                          : 'text-graphite-700 hover:bg-surface-sunk hover:text-graphite-900')
                      }
                    >
                      {t.label}
                    </button>
                  </li>
                );
              })}
              {signedIn ? (
                <>
                  <li role="separator" className="my-1 border-t border-graphite-100" />
                  {userEmail ? (
                    <li
                      role="none"
                      className="px-3 py-1 font-mono text-[0.65rem] text-graphite-500"
                    >
                      {userEmail}
                    </li>
                  ) : null}
                  <li role="none">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => void onSignOut()}
                      disabled={signingOut}
                      className="block w-full px-3 py-2 text-left font-sans text-sm text-graphite-700 transition-colors hover:bg-surface-sunk hover:text-graphite-900 disabled:opacity-50"
                    >
                      {signingOut ? 'Signing out…' : 'Sign out'}
                    </button>
                  </li>
                </>
              ) : null}
            </ul>
          ) : null}
        </div>
      </div>
    </nav>
  );
}

// Sprint lock: while a sprint is active, hide every other navigation
// affordance. Only the SprintRunner UI + a top-right two-click "Exit
// sprint" remain. Two-click confirm prevents fat-finger exits mid-call.
function SprintShell({
  contacts,
  onExit,
}: {
  contacts: readonly ZohoContact[];
  onExit: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  // Reset confirm if the user doesn't follow through within 4s.
  useEffect(() => {
    if (!confirming) return;
    const t = setTimeout(() => setConfirming(false), 4000);
    return () => clearTimeout(t);
  }, [confirming]);

  const onClickExit = () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    // Pending writes retry server-side via the cron drain; SprintRunner's
    // SyncGate (Done button on summary) handles the in-flow case. This
    // top-right escape hatch is for "I need out NOW" — don't gate it.
    onExit();
  };

  return (
    <div className="relative min-h-full">
      <div className="pointer-events-none fixed top-3 right-4 z-30">
        <button
          type="button"
          onClick={onClickExit}
          className={
            'pointer-events-auto rounded-md px-3 py-1.5 font-sans text-sm transition-colors ' +
            (confirming
              ? 'bg-signal-amber text-surface hover:bg-signal-amber-hover'
              : 'border border-graphite-300 bg-surface text-graphite-700 hover:bg-surface-sunk')
          }
        >
          {confirming ? 'Confirm exit?' : 'Exit sprint'}
        </button>
      </div>

      <SprintRunner contacts={contacts} onExit={onExit} />
    </div>
  );
}
